import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Form, FormInput } from '@praktio/common-ui';
import { Helmet } from 'react-helmet';

import UnauthenticatedWrapper from '../UnauthenticatedWrapper/UnauthenticatedWrapper';
import { RootState } from '../../../ducks/store';
import { useQuery } from '../../../utils/functions';
import { TenantApiUrl } from '../../../utils/api';
import { GenericFormWarning, PlatformMessageTypes } from '../../../utils/constants';

import './LoginSSO.scss';

const ComponentId = 'LOGIN_SSO';

interface IErrors {
  [key: string]: string | undefined;
}

const SSOWarning = {
  title: 'Login Error',
  body: 'Email not configured for SSO. Please contact support@praktio.com.',
};

const LoginSSO = () => {
  const location = useLocation();
  const query = useQuery();
  const errorCode = query.get('errorCode');
  const from = (location as any)?.state?.from?.pathname || '/dashboard';
  const [form, setForm] = useState({
    email: '',
  });
  const [errors, setErrors] = useState<IErrors>();
  const [isSubmitting] = useState(false);
  const isMounted = useRef(false);

  const { isAuthenticated, messages } = useSelector((state: RootState) => {
    const { isAuthenticated } = state.auth;
    return {
      isAuthenticated,
      messages: state.platformData.messages.filter((m) => m.componentId?.includes(ComponentId)),
    };
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to={from} />;
  }

  const setField = (field: string, value: string) => {
    setForm({
      ...form,
      [field]: value,
    });
    if (errors && !!errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const preventSubmit = isSubmitting || form.email.length < 1;
  let code: number | undefined;
  if (errorCode && !isNaN(parseFloat(errorCode))) {
    code = parseFloat(errorCode);
  }

  const alert = code
    ? messages.find(
        (m) =>
          m.type === PlatformMessageTypes.Alert &&
          m.componentId?.includes(ComponentId) &&
          m.errorCode === code,
      ) || GenericFormWarning
    : null;

  return (
    <UnauthenticatedWrapper formName="Log in to your account using SSO">
      <Helmet>
        <title>Login - Praktio</title>
      </Helmet>
      {alert && (
        <div key={`warning-token`} className="alert alert-danger" role="alert">
          <div className="alert-title">{alert.title}</div>
          <div className="alert-body">{alert.body}</div>
        </div>
      )}
      <Form
        formId="login-sso-form"
        className="login-sso-form"
        onSubmit={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <FormInput
          formGroupClassName="LoginEmail"
          value={form.email}
          fieldName="email"
          label="Email"
          placeholder="Email"
          onChange={(e: any) => setField('email', e.target.value.replaceAll('’', `'`))}
          error={errors?.email}
        />
        <div className="login-button-wrapper">
          <Button
            form="login-form"
            type="submit"
            btnStyle="primary"
            disabled={preventSubmit}
            showSpinner={isSubmitting}
            overlaySpinner
            onClick={(e: any) => {}}
            href={`${TenantApiUrl}/common/auth/sso?email=${encodeURIComponent(form.email)}`}
          >
            NEXT
          </Button>
        </div>
        <div className="form-footer">
          By logging in, you agree to Praktio's{' '}
          <a href="https://praktio.com/pages/terms-of-service">Terms of Service</a>, along with our{' '}
          <a href="https://praktio.com/pages/privacy-policy">Privacy Policy</a>. Our site uses
          cookies, as further detailed in our Privacy Policy, and you consent to such use by logging
          in.
        </div>
      </Form>
    </UnauthenticatedWrapper>
  );
};

export default LoginSSO;
